<template>
  <div
    class="home__wrapper"
    :class="siteVariant"
  >
    <div class="extended-deadline">
      <p>The competition deadline has been extended to May 2!</p>
    </div>
    <IntroTitle :site-variant="siteVariant">
      <div class="hero-img">
        <img
          v-if="siteVariant === 'acura'"
          src="@/assets/img/hero-acura.webp"
          alt="Acura ZDX"
        >
        <img
          v-if="siteVariant === 'honda'"
          src="@/assets/img/hero-honda.webp"
          alt="Honda Prologue"
        >
      </div>
    </IntroTitle>
    <IntroSection :site-variant="siteVariant" />
    <StorytellingVideo :site-variant="siteVariant" />
    <PrizesContainer :site-variant="siteVariant" />
    <DateList :site-variant="siteVariant" />
    <GlowButton
      class="get-started-btn"
      @click="openModal"
      :disabled="disabledContest"
      aria-label="Click to open competition rules and registration form"
    >
      Get Started
      <div
        v-if="disabledContest"
        class="tooltip"
        role="tooltip"
      >
        <p>The submission deadline has passed.</p>
      </div>
    </GlowButton>
    <TheModal class="modal-wrapper" v-model="isModalOpen">
      <SubmissionModal :site-variant="siteVariant" @closeModal="closeModal"/>
    </TheModal>
  </div>
</template>

<script setup>
import TheModal from '@/components/TheModal.vue';
import IntroTitle from '@/components/intro/IntroTitle.vue';
import IntroSection from '@/components/intro/IntroSection.vue';
import PrizesContainer from '@/components/home/PrizesContainer.vue';
import StorytellingVideo from '@/components/home/StorytellingVideo.vue';
import DateList from '@/components/home/DateList.vue';
import GlowButton from '@/components/GlowButton.vue';
import SubmissionModal from '@/components/submission/SubmissionModal.vue';
import { computed, ref } from 'vue';

defineProps({
  siteVariant: String,
});

const currentDate = new Date();
const submissionDeadline = new Date('2025-05-05T00:00:00-03:59');

const disabledContest = computed(() => currentDate >= submissionDeadline);

const isModalOpen = ref(false);

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<style lang="scss" scoped>
.home__wrapper {
  .extended-deadline {
    background-color: $AC_Orange;
    padding: 10px 24px;

    @media (min-width: 1281px) {
      padding-top: 20px;
      padding-bottom: 20px;
    }

    p {
      font-size: clamp(18px, 47px, 2.75vw);
      font-weight: 700;
      color: $AC_White;
      letter-spacing: 0.5px;
      line-height: 1.2;
      text-transform: uppercase;

      @media (min-width: 1281px) {
        letter-spacing: 1.5px;
      }
    }
  }

  .hero-img {
    max-width: fit-content;
    margin: 34px auto 0;
    position: relative;

    @media (min-width: 641px) {
      margin-top: 50px;
    }

    img {
      position: relative;
      z-index: 1;
    }

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 0;
      width: 100%;
      height: 15vh;
      background-image: url('@/assets/img/spotlight-shadow.webp');
      background-position: center;
      background-size: 115% 170%;
      background-repeat: no-repeat;
      z-index: 0;

      @media (min-width: 641px) {
        height: 25vh;
        background-size: 110% 150%;
      }

      @media (min-width: 1025px) {
        height: 37vh;
      }
    }
  }

  .get-started-btn {
    &:hover,
    &:focus {
      .tooltip {
        visibility: visible;
      }
    }
  }

  .tooltip {
    visibility: hidden;
    position: absolute;
    top: 112%;
    left: 50%;
    transform: translateX(-50%);
    background-color: $AC_Primary;
    border-radius: 4px;
    width: 100%;
    max-width: 375px;
    padding: 12px;

    &::before {
      content: '';
      border: 10px solid transparent;
      border-bottom-color: $AC_Primary;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 100%;
    }

    p {
      font-size: 18px;
      letter-spacing: 0.5px;
      line-height: 1.2;
      color: $AC_White;
      text-transform: none;
    }
  }

  &.acura {
    font-family: 'Avenir', sans-serif;
    font-weight: 500;

    button {
      font-family: 'Avenir', sans-serif;
      font-weight: normal;
    }

    .tooltip p {
      font-family: 'Avenir Book';
    }
  }

  &.honda {
    font-family: 'Myriad Pro', sans-serif;
    font-weight: 500;

    button {
      font-family: 'Myriad Pro', sans-serif;
      font-weight: 700;
    }

    .tooltip p {
      font-family: 'Myriad Pro';
    }
  }
}
</style>
